import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  emailQueueService,
  EMAIL_STATUS_LABELS,
  type EmailQueueStatistics,
  type EmailQueueItem,
  type PagedEmailQueueResult,
} from '../../services/emailQueueService';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  EnvelopeIcon,
  XCircleIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const AUTO_REFRESH_INTERVAL = 15_000;

const STATUS_BADGE: Record<string, string> = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Processing: 'bg-blue-100 text-blue-800',
  Sent: 'bg-green-100 text-green-800',
  Failed: 'bg-red-100 text-red-800',
  Cancelled: 'bg-gray-100 text-gray-500',
};

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}

export const EmailQueuePage: React.FC = () => {
  const [stats, setStats] = useState<EmailQueueStatistics | null>(null);
  const [emails, setEmails] = useState<PagedEmailQueueResult | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, emailsRes] = await Promise.all([
        emailQueueService.getStatistics(),
        emailQueueService.getEmails({
          status: statusFilter || undefined,
          search: searchFilter || undefined,
          page,
          pageSize,
        }),
      ]);
      setStats(statsRes);
      setEmails(emailsRes);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Erro ao carregar fila de e-mails:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchFilter, page, pageSize]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(fetchData, AUTO_REFRESH_INTERVAL);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, fetchData]);

  const handleResend = async (id: string) => {
    try {
      await emailQueueService.resend(id);
      await fetchData();
    } catch { /* handled */ }
  };

  const handleCancel = async (id: string) => {
    try {
      await emailQueueService.cancel(id);
      await fetchData();
    } catch { /* handled */ }
  };

  const handleCancelSelected = async () => {
    if (selected.size === 0) return;
    try {
      await emailQueueService.cancelMany(Array.from(selected));
      setSelected(new Set());
      await fetchData();
    } catch { /* handled */ }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!emails) return;
    const allIds = emails.items.map(e => e.id);
    const allSelected = allIds.every(id => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  const handleFilterChange = () => {
    setPage(1);
    setSelected(new Set());
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fila de E-mails</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie o envio de e-mails, reenvie falhos e acompanhe o status em tempo real.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-400">
              Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')}
            </span>
          )}
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
              autoRefresh
                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <ClockIcon className="h-4 w-4" />
            {autoRefresh ? 'Auto 15s' : 'Pausado'}
          </button>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <KpiCard
            label="Pendentes"
            value={stats.pending}
            icon={<ClockIcon className="h-6 w-6 text-yellow-600" />}
            bg="bg-yellow-50"
            active={statusFilter === 'Pending'}
            activeBorder="ring-yellow-400"
            onClick={() => { setStatusFilter(statusFilter === 'Pending' ? '' : 'Pending'); handleFilterChange(); }}
          />
          <KpiCard
            label="Processando"
            value={stats.processing}
            icon={<ArrowPathIcon className="h-6 w-6 text-blue-600" />}
            bg="bg-blue-50"
            active={statusFilter === 'Processing'}
            activeBorder="ring-blue-400"
            onClick={() => { setStatusFilter(statusFilter === 'Processing' ? '' : 'Processing'); handleFilterChange(); }}
          />
          <KpiCard
            label="Enviados"
            value={stats.sent}
            icon={<CheckCircleIcon className="h-6 w-6 text-green-600" />}
            bg="bg-green-50"
            active={statusFilter === 'Sent'}
            activeBorder="ring-green-400"
            onClick={() => { setStatusFilter(statusFilter === 'Sent' ? '' : 'Sent'); handleFilterChange(); }}
          />
          <KpiCard
            label="Falhos"
            value={stats.failed}
            icon={<XCircleIcon className="h-6 w-6 text-red-600" />}
            bg="bg-red-50"
            active={statusFilter === 'Failed'}
            activeBorder="ring-red-400"
            onClick={() => { setStatusFilter(statusFilter === 'Failed' ? '' : 'Failed'); handleFilterChange(); }}
          />
          <KpiCard
            label="Cancelados"
            value={stats.cancelled}
            icon={<NoSymbolIcon className="h-6 w-6 text-gray-500" />}
            bg="bg-gray-50"
            active={statusFilter === 'Cancelled'}
            activeBorder="ring-gray-400"
            onClick={() => { setStatusFilter(statusFilter === 'Cancelled' ? '' : 'Cancelled'); handleFilterChange(); }}
          />
        </div>
      )}

      {/* Filters + Batch Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
            <input
              type="text"
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleFilterChange(); }}
              placeholder="Destinatário ou assunto..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); handleFilterChange(); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos</option>
              {Object.entries(EMAIL_STATUS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          {selected.size > 0 && (
            <button
              onClick={handleCancelSelected}
              className="flex items-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 text-sm transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
              Cancelar {selected.size} selecionado(s)
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={toggleSelectAll}
                    checked={emails != null && emails.items.length > 0 && emails.items.every(e => selected.has(e.id))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatário</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assunto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origem</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tentativas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && !emails ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-blue-500 mb-2" />
                    <span className="text-sm text-gray-500">Carregando...</span>
                  </td>
                </tr>
              ) : emails && emails.items.length > 0 ? (
                emails.items.map(email => (
                  <EmailRow
                    key={email.id}
                    email={email}
                    isSelected={selected.has(email.id)}
                    onToggleSelect={() => toggleSelect(email.id)}
                    onResend={handleResend}
                    onCancel={handleCancel}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                    Nenhum e-mail encontrado para os filtros selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {emails && emails.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              {emails.totalCount} registro(s) — Página {emails.page} de {emails.totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
              >
                Anterior
              </button>
              <button
                disabled={page >= emails.totalPages}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function KpiCard({
  label,
  value,
  icon,
  bg,
  active,
  activeBorder,
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  bg: string;
  active?: boolean;
  activeBorder?: string;
  onClick?: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
      className={`rounded-lg p-4 ${bg} flex items-center gap-4 cursor-pointer transition-all select-none
        ${active ? `ring-2 ${activeBorder} shadow-md scale-[1.02]` : 'hover:shadow-sm hover:scale-[1.01]'}`}
    >
      <div className="shrink-0">{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value.toLocaleString('pt-BR')}</p>
        <p className="text-xs text-gray-600">{label}</p>
      </div>
    </div>
  );
}

function EmailRow({
  email,
  isSelected,
  onToggleSelect,
  onResend,
  onCancel,
}: {
  email: EmailQueueItem;
  isSelected: boolean;
  onToggleSelect: () => void;
  onResend: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const badgeClass = STATUS_BADGE[email.status] ?? 'bg-gray-100 text-gray-600';
  const hasDetails = email.errorMessage || email.body;

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => hasDetails && setExpanded(v => !v)}
      >
        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        </td>
        <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate" title={email.to}>
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="h-4 w-4 text-gray-400 shrink-0" />
            {email.to}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-[250px] truncate" title={email.subject}>
          {email.subject}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">{email.origin ?? '-'}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
            {EMAIL_STATUS_LABELS[email.status] ?? email.status}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {email.attemptCount}/{email.maxAttempts}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(email.createdAt)}</td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
            {(email.status === 'Failed' || email.status === 'Cancelled') && (
              <button
                onClick={() => onResend(email.id)}
                title="Reenviar"
                className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            )}
            {(email.status === 'Pending' || email.status === 'Failed') && (
              <button
                onClick={() => onCancel(email.id)}
                title="Cancelar"
                className="p-1.5 rounded hover:bg-red-50 text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="px-4 py-3 bg-gray-50">
            {email.errorMessage && (
              <div className="flex items-start gap-2 mb-3">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-red-700 mb-1">Erro:</p>
                  <pre className="text-xs text-red-600 whitespace-pre-wrap break-all max-h-32 overflow-y-auto bg-red-50 p-2 rounded">
                    {email.errorMessage}
                  </pre>
                  {email.errorStackTrace && (
                    <details className="mt-2">
                      <summary className="text-xs text-red-500 cursor-pointer">Stack Trace</summary>
                      <pre className="text-xs text-red-400 whitespace-pre-wrap break-all max-h-40 overflow-y-auto mt-1">
                        {email.errorStackTrace}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Corpo do e-mail:</p>
              {email.isHtml ? (
                <div
                  className="text-xs text-gray-700 max-h-48 overflow-y-auto bg-white p-3 rounded border border-gray-200"
                  dangerouslySetInnerHTML={{ __html: email.body }}
                />
              ) : (
                <pre className="text-xs text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white p-3 rounded border border-gray-200">
                  {email.body}
                </pre>
              )}
            </div>
            {email.sentAt && (
              <p className="text-xs text-gray-400 mt-2">Enviado em: {formatDate(email.sentAt)}</p>
            )}
            {email.hangfireJobId && (
              <p className="text-xs text-gray-400 mt-1">Hangfire Job ID: {email.hangfireJobId}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default EmailQueuePage;
